import { createServerFn } from "@tanstack/react-start";

/**
 * The public demo is one shared account. Anyone can walk in, everything is editable,
 * and a nightly job wipes it back to the same sample history. Nothing here touches a
 * real member's data: the account owns its own rows and holds no admin role.
 */
export const DEMO_EMAIL = "demo@thefoundersapp.com";

/**
 * Opens the door: makes sure the demo account exists and is stocked, then hands back the
 * throwaway demo credentials the browser signs in with. Deliberately public — the account
 * is a sandbox that gets emptied every night.
 */
export const enterDemo = createServerFn({ method: "POST" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const password = process.env["DEMO_ACCOUNT_PASSWORD"];
  if (!password) throw new Error("The demo isn't available right now.");

  // Light throttle so the door can't be used as a free account-creation hammer.
  const { data: limit } = await supabaseAdmin.rpc("rate_limit_hit", {
    _bucket: "demo_enter",
    _key: "global",
    _limit: 600,
    _window_seconds: 60,
  });
  if ((limit as { allowed?: boolean } | null)?.allowed === false) {
    throw new Error("The demo is busy right now. Try again in a moment.");
  }

  let userId: string | null = null;

  const { data: existing } = await supabaseAdmin
    .from("profiles")
    .select("user_id")
    .eq("is_demo", true)
    .limit(1)
    .maybeSingle();

  if (existing?.user_id) {
    userId = existing.user_id;
  } else {
    const { data: created } = await supabaseAdmin.auth.admin.createUser({
      email: DEMO_EMAIL,
      password,
      email_confirm: true,
    });

    if (created?.user?.id) {
      userId = created.user.id;
    } else {
      // Already registered from an earlier boot — find it by email.
      const { data: list } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 200 });
      const found = list?.users?.find((u) => u.email?.toLowerCase() === DEMO_EMAIL);
      if (!found) throw new Error("Could not open the demo.");
      userId = found.id;
    }
  }

  // Keep the account exactly what it claims to be: marked as the demo, never an admin,
  // password matching the one handed out, and stocked with the sample history.
  await supabaseAdmin
    .from("profiles")
    .upsert({ user_id: userId, email: DEMO_EMAIL, is_demo: true }, { onConflict: "user_id" });
  await supabaseAdmin.from("user_roles").delete().eq("user_id", userId);
  await supabaseAdmin.auth.admin.updateUserById(userId, { password, email_confirm: true });

  const { count } = await supabaseAdmin
    .from("businesses")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .is("deleted_at", null);
  if (!count) await supabaseAdmin.rpc("reset_demo_data");

  return { email: DEMO_EMAIL, password };
});
